import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Minus, Plus, Check } from "lucide-react";
import api, { brl, asProduct } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("ingredientes");

  useEffect(() => {
    if (!id || id === "undefined") {
      setProduct(false);
      return;
    }
    api
      .get(`/products/${id}`)
      .then((r) => setProduct(asProduct(r.data) || false))
      .catch(() => setProduct(false));
  }, [id]);

  if (product === false) return <div className="pt-40 text-center font-serif text-3xl text-ink/50">Produto não encontrado.</div>;
  if (!product) return <div className="pt-40 text-center font-mono text-xs uppercase tracking-widest text-ink/40">Carregando...</div>;

  const tabItems = tab === "ingredientes" ? product.ingredients : product.benefits;

  return (
    <div className="mx-auto max-w-[1280px] px-4 pb-24 pt-10 sm:px-6">
      <Link to="/loja" className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ink/60 hover:text-clay">
        <ArrowLeft size={14} /> Voltar à loja
      </Link>
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="relative aspect-[4/5] overflow-hidden bg-sand-elevated">
          <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          {product.bestseller && <span className="absolute left-5 top-5 bg-clay px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-white">Mais Vendido</span>}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-clay">{product.tagline}</p>
          <h1 className="mt-3 font-serif text-4xl font-light leading-tight text-forest-deep lg:text-5xl">{product.name}</h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex items-center gap-1 text-ochre">
              {[...Array(5)].map((_, i) => <Star key={i} size={15} fill="currentColor" strokeWidth={0} />)}
            </div>
            <span className="font-mono text-xs text-ink/60">{product.rating} · avaliação</span>
          </div>
          <p className="mt-6 font-sans text-base leading-relaxed text-ink/80">{product.description}</p>
          {product.sale_price != null && product.sale_price < product.price ? (
            <div className="mt-8 flex items-baseline gap-3">
              <p className="font-serif text-4xl text-forest-deep">{brl(product.sale_price)}</p>
              <p className="font-sans text-xl text-ink/40 line-through">{brl(product.price)}</p>
              <span className="rounded-full bg-ochre px-2.5 py-1 font-sans text-xs font-bold text-forest-deep">-{Math.round((1 - product.sale_price / product.price) * 100)}%</span>
            </div>
          ) : (
            <p className="mt-8 font-serif text-4xl text-forest-deep">{brl(product.price)}</p>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-borderlight">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="detail-qty-dec" className="px-4 py-4 hover:text-clay"><Minus size={15} /></button>
              <span className="px-4 font-mono text-sm" data-testid="detail-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="detail-qty-inc" className="px-4 py-4 hover:text-clay"><Plus size={15} /></button>
            </div>
            <button onClick={() => add({ ...product, price: (product.sale_price != null && product.sale_price < product.price) ? product.sale_price : product.price }, qty)} data-testid="detail-add-to-cart"
              className="flex-1 bg-forest-deep py-4 font-mono text-xs uppercase tracking-[0.2em] text-alabaster transition-colors hover:bg-clay">
              Adicionar à sacola
            </button>
          </div>

          <div className="mt-12 border-t border-borderlight pt-8">
            <div className="flex gap-6">
              {[["ingredientes", "Ingredientes"], ["beneficios", "Benefícios"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                  className={`font-mono text-[11px] uppercase tracking-widest transition-colors ${tab === k ? "text-clay" : "text-ink/50 hover:text-ink"}`}>
                  {l}
                </button>
              ))}
            </div>
            <ul className="mt-6 space-y-3">
              {tabItems.length === 0 ? (
                <li className="font-sans text-sm text-ink/50">Nenhuma informação disponível.</li>
              ) : (
                tabItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 font-sans text-sm text-ink/80">
                    <Check size={16} className="mt-0.5 flex-shrink-0 text-clay" /> {item}
                  </li>
                ))
              )}
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
